import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { AbstractControl, ReactiveFormsModule, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { I18nKeys } from '@jump-tech-frontend/app-config';
import { JUI_INPUT_IMPORTS } from '@jump-tech-frontend/core-components-angular';
import { DnoAreaIds, QuestionBase } from '@jump-tech-frontend/domain';
import { QuestionHintComponent, QuestionLabelContentComponent } from '@jump-tech-frontend/question-components';
import { BaseQuestionComponent } from '../question.component';

export const MPAN_PATTERN = /^(?:(?:81|10)\d{9}|\d{13})$/;

export const MPAN_MAX_LENGTH = 13;

const MPAN_DISTRIBUTOR_ID_LENGTH = 2;

const MPAN_DISTRIBUTOR_IDS: readonly string[] = Object.keys(DnoAreaIds).filter(id => id !== '81');

const FULL_MPAN_LENGTH = 21;

export const MPAN_INPUT_MAX_LENGTH = 30;

const SEPARATORS_ONLY = /^[\d\s\-/]+$/;

/** Prime weights applied to the first 12 digits of a 13-digit core GB MPAN for Modulo 11 check digit. */
const MPAN_CHECK_DIGIT_WEIGHTS = [3, 5, 7, 13, 17, 19, 23, 29, 31, 37, 41, 43];

export const MPAN_DEFAULT_LABEL = 'Enter your supply number (or MPRN/MPAN).';

export const MPAN_DEFAULT_ERROR_MESSAGE = 'Please enter the 13 digits of your MPAN, or 11 if MPRN';

export const MPAN_CHECK_DIGIT_ERROR_MESSAGE =
  'That supply number doesn’t look right. Please check the 13 digits against your bill.';

export const MPAN_DISTRIBUTOR_ERROR_MESSAGE =
  'We don’t recognise the first two digits of that supply number. Please check them against your bill.';

@Component({
  selector: `crds-question-mpan-input`,
  template: `
    @if (show && form) {
      <div [formGroup]="form">
        @if (this.useJuiControls) {
          <jui-input
            [size]="controlSize"
            type="text"
            [formControlName]="question.key"
            [label]="label"
            [dataqastring]="question.key + 'Input'"
            [required]="question.required ?? false"
            [readonly]="question.readOnly"
            [maxlength]="maxLength"
            [disable]="form.get(question.key).disabled"
            [errors]="form.get(question.key)?.errors"
            [messages]="messages"
            [tooltip]="buildTooltip()"
            (juiChange)="onChange()"
            [validation]="'always'"
          ></jui-input>
          @if (question.permanentHint) {
            <question-hint [question]="question"></question-hint>
          }
        } @else {
          <!-- QUESTION LABEL -->
          <label class="form-label" [attr.for]="question.key" [attr.data-qa]="question.key">
            <question-label-content
              [question]="displayQuestion"
              [isInvalid]="isInvalid"
              [i18ns]="i18ns"
            ></question-label-content>
          </label>
          <!-- QUESTION INPUT -->
          <input
            [formControlName]="question.key"
            [id]="question.key"
            type="text"
            inputmode="numeric"
            [readOnly]="question.readOnly"
            (change)="onChange()"
            class="form-control mb-2"
            [attr.data-qa]="question.key + 'Input'"
            [attr.maxlength]="maxLength"
            [placeholder]="question.placeholder ?? ''"
          />
          <!-- QUESTION HINT -->
          <question-hint [question]="question"></question-hint>
        }
      </div>
    }
  `,
  styles: [
    `
      [readonly] {
        color: -internal-light-dark(rgb(84, 84, 84), rgb(170, 170, 170));
        cursor: default;
        background-color: -internal-light-dark(rgba(239, 239, 239, 0.3), rgba(59, 59, 59, 0.3));
        border-color: rgba(118, 118, 118, 0.3);
        pointer-events: none;
      }
    `
  ],
  imports: [ReactiveFormsModule, QuestionLabelContentComponent, QuestionHintComponent, ...JUI_INPUT_IMPORTS]
})
export class MpanInputQuestionComponent extends BaseQuestionComponent implements OnChanges {
  @Input() override form: UntypedFormGroup;
  @Input() override question: QuestionBase<string>;
  @Input() i18ns: I18nKeys;
  @Input() useJuiControls?: boolean = false;
  @Input() deviceContext?: string = 'desktop';

  controlSize = 'sm';
  readonly maxLength = MPAN_INPUT_MAX_LENGTH;

  private validatorApplied = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.question || !Object.prototype.hasOwnProperty.call(changes, 'question')) {
      return;
    }

    this.controlSize = this.getControlSize();
    this.applyMpanValidator();

    if (this.question.disabled === true && !this.show) {
      setTimeout(() => this.form?.get(this.question.key)?.disable());
    }
  }

  get label(): string {
    return this.question.label || MPAN_DEFAULT_LABEL;
  }

  get errorMessage(): string {
    const controlErrors = this.form?.get(this.question.key)?.errors;
    if (controlErrors?.['mpanCheckDigit']) {
      return MPAN_CHECK_DIGIT_ERROR_MESSAGE;
    }
    if (controlErrors?.['mpanDistributor']) {
      return MPAN_DISTRIBUTOR_ERROR_MESSAGE;
    }
    return MPAN_DEFAULT_ERROR_MESSAGE;
  }

  get messages(): { required: string; mpanFormat: string; mpanCheckDigit: string; mpanDistributor: string } {
    return {
      required: this.i18ns?.required ?? MPAN_DEFAULT_ERROR_MESSAGE,
      mpanFormat: MPAN_DEFAULT_ERROR_MESSAGE,
      mpanCheckDigit: MPAN_CHECK_DIGIT_ERROR_MESSAGE,
      mpanDistributor: MPAN_DISTRIBUTOR_ERROR_MESSAGE
    };
  }

  get displayQuestion(): QuestionBase<string> {
    return { ...this.question, label: this.label, errorMessage: this.errorMessage } as QuestionBase<string>;
  }

  override get isInvalid(): boolean {
    const control = this.form?.get(this.question.key);
    return (control?.touched && control?.invalid) ?? false;
  }

  override get show() {
    const showIfQuestionPopulated = this.question.showIf ? this.showIf : true;
    return (
      (this.question.showIfPopulated !== true || this.value) && showIfQuestionPopulated && this.question.show !== false
    );
  }

  onChange(): void {
    const control = this.form?.get(this.question.key);
    if (typeof control?.value === 'string') {
      const normalised = this.normaliseSupplyNumber(control.value);

      if (normalised !== control.value) {
        control.setValue(normalised, { emitEvent: false });
        control.markAsTouched();
        control.updateValueAndValidity({ emitEvent: false });
      }
    }
  }

  buildTooltip(): string {
    return this.question.hint && !this.question.permanentHint ? this.question.hint : '';
  }

  getControlSize(): string {
    return this.deviceContext === 'mobile' ? 'lg' : 'sm';
  }

  private applyMpanValidator(): void {
    const control = this.form?.get(this.question.key);
    if (!control || this.validatorApplied) {
      return;
    }
    control.clearValidators();
    if (this.question.required) {
      control.addValidators(Validators.required);
    }
    control.addValidators(this.mpanValidator);
    control.updateValueAndValidity({ emitEvent: false });
    this.validatorApplied = true;
  }

  private mpanValidator = (control: AbstractControl): ValidationErrors | null => {
    const value = this.normaliseSupplyNumber(control.value as string);
    if (!value) {
      return null;
    }

    if (!MPAN_PATTERN.test(value)) {
      return { mpanFormat: true };
    }

    if (value.length === MPAN_MAX_LENGTH) {
      if (!MPAN_DISTRIBUTOR_IDS.includes(value.slice(0, MPAN_DISTRIBUTOR_ID_LENGTH))) {
        return { mpanDistributor: true };
      }

      if (!this.hasValidMpanCheckDigit(value)) {
        return { mpanCheckDigit: true };
      }
    }

    return null;
  };

  /**
   * Reduces anything the user gives us to the value we want to store - the core
   * MPAN / MPRN. Separators are dropped and a full 21 digit MPAN is reduced to its
   * bottom line. Values containing anything other than digits and separators are
   * returned trimmed but otherwise untouched, so validation can reject them.
   */
  private normaliseSupplyNumber(raw: string | null | undefined): string {
    const value = (raw ?? '').trim();
    if (!value || !SEPARATORS_ONLY.test(value)) {
      return value;
    }
    const digits = value.replace(/\D/g, '');
    return digits.length === FULL_MPAN_LENGTH ? digits.slice(-MPAN_MAX_LENGTH) : digits;
  }

  /** Modulus 11 check digit calculation for 13-digit GB MPANs */
  private hasValidMpanCheckDigit(core: string): boolean {
    if (!/^\d{13}$/.test(core)) {
      return false;
    }
    let sum = 0;
    for (let i = 0; i < MPAN_CHECK_DIGIT_WEIGHTS.length; i++) {
      sum += Number(core[i]) * MPAN_CHECK_DIGIT_WEIGHTS[i];
    }
    const remainder = sum % 11;
    if (remainder === 10) {
      return false;
    }
    return remainder === Number(core[MPAN_MAX_LENGTH - 1]);
  }
}
